import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import config from '@iotv/config';
import { between, existsFilter } from '@iotv/core-services';
import { edgeTypeMatrix, getGroupToEntityEdgeTypeId, getNiceDate, isDeviceContext, isOnDemandProgrammeConstructorProps, NodeEdgeMapper, summarizeList, } from '@iotv/datamodel-core';
import { AdjacentType, ComparisonType, getZombieInstanceFromPrimaryKey, isNonNaNNumber, LogicalOperatorType, } from '@iotv/iotv-v3-types';
import { Skeleton, Stack, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { SimpleTreeView } from '@mui/x-tree-view';
import { useCallback, useContext, useEffect, useReducer, useRef } from 'react';
import { v4 } from 'uuid';
import { getSingleTypeRecursivePath } from '../../../../../../../../components/EnititySupport/RecusiveEntityGroup/getRecursiveQueryPath';
import { radioSelector, SelectAll } from '../../../../../../../../components/factories/DataGrid';
import { AppMainViewContext } from '../../../../../../../../ContextProviders/AppContextProvider';
import { NodeEdgeMapperContext } from '../../../../../../../../ContextProviders/NodeEdgeMapperComponentContext';
import { subscribeToMQTTChannel } from '../../../../../../../../data/daoFunctions/daoFunctions';
import { isAdminRole } from '../../../../../../../../helpers/access';
import { pluralize, toTitleCase } from '../../../../../../../../helpers/format';
import { useSelectedObjects } from '../../../../../../../../hooks/useSelectedObjects';
import { Severity } from '../../../../../../../../types/AppTypes';
import { ObjectTree } from '../../../../../ObjectTree';
import { AdminCardWrapper } from '../../../../common/AdminCardWrapper';
import { ItemTreeOnClickWrapper } from '../../../../common/ItemTreeOnClickWrapper';
import { ProgrammeHeader } from '../../../../common/ProgrammeHeader';
import { Timezone } from '../../../../common/Timezone';
import { EntityProgammeTimelineChart } from '../../../../EntityProgamme/Components/EntityTimelineChart';
import { TransactionIndicators } from '../../../../EntityProgamme/Components/TransactionStateIndicators';
import { getDataTable } from '../../../fns/getDataTable';
import { getScheduleContextInfo } from '../../../fns/scheduleContextInfo';
import { OnDemandProgrammeContext, } from '../../../Reducers/OnDemandProgrammeReducer';
import { Actions } from './Actions';
import { getDataGridRowClassForWindow, getStylesForColorCodes } from './colorCodes';
import { Controls } from './Controls';
import { ScheduleStatus } from './ScheduleStatus';
const debug = process.env.REACT_APP_DEBUG && false;
const TransationStateReducer = (prev, next) => {
    return { ...prev, ...next };
};
export const WindowAllocation = () => {
    'use no memo';
    const [{ userSelectedGroup, contextCustomer, contextUser, isSmallView, UserFunctions }] = useContext(AppMainViewContext);
    const isAdmin = isAdminRole(userSelectedGroup);
    const [state, dispatch] = useContext(OnDemandProgrammeContext);
    const { programme, scheduler, schedulerHash, tableRows, gridRows } = state;
    const [transactionState, dispatchTransactionState] = useReducer(TransationStateReducer, { loading: false, verifying: false, saving: false });
    const { state: { nemHash, nodeEdgeMapper }, dispatch: nodeEdgeMapperDispatch, } = useContext(NodeEdgeMapperContext);
    const deviceConfigSubscription = useRef(undefined);
    useEffect(() => {
        if (contextUser) {
            const deviceConfigTopic = config.aws.mqtt.deviceConfigUpdateChannel.replace('[deviceSk]', '#');
            deviceConfigSubscription.current = subscribeToMQTTChannel(contextUser, deviceConfigTopic, (data) => {
                const receivedObject = data.value;
                debug && console.log('Got message on device config channel', receivedObject);
                if (isDeviceContext(receivedObject)) {
                    debug && console.log('Updating NEM with deviceContext', receivedObject);
                    const action = {
                        type: 'loadItems',
                        value: { items: [receivedObject], canaryString: `OnDemandProgramme WindowAllocation loaded updated deviceContext` },
                    };
                    nodeEdgeMapperDispatch(action);
                }
            });
        }
        return () => {
            deviceConfigSubscription.current?.unsubscribe();
        };
    }, [contextUser?.sk]);
    const objectTreeNodeEdgeMapperRef = useRef(new NodeEdgeMapper());
    const selectableGroups = objectTreeNodeEdgeMapperRef.current.getNodesOfType('Group');
    const selectableItems = programme ? objectTreeNodeEdgeMapperRef.current.getNodesOfType(programme.supportedType) : [];
    const selectableGroupsAndItems = [...selectableGroups, ...selectableItems];
    const togglePresenceInSelectedObjectsShim = (v, id) => {
        toggleWindow(id);
    };
    const { state: [[selectedItems, setSelectedItems]], } = useSelectedObjects([], selectableGroupsAndItems);
    const { state: [[selectedWindows, setSelectedWindows]], handlers: { isInSelectedObjects: inSelectedWindows, addToSelectedObjects: addSelectedWindows, removeFromSelectedObjects: removeSelectedWindow, togglePresenceInSelectedObjects: toggleWindow, }, } = useSelectedObjects([], gridRows ?? [], false);
    /** on change of selected windows */
    useEffect(() => {
        if (selectedWindows.length === 1) {
            const items = scheduler?.getItemsForWindow(selectedWindows[0].window);
            setSelectedItems(items ?? []);
        }
        else {
            setSelectedItems([]);
        }
    }, [selectedWindows]);
    const cardPadding = 16;
    const headerHeight = 48;
    const tabPadding = 8;
    const entities = scheduler?.getAllItems() ?? [];
    const getEntitySchedulesMatchesRequestedReported = useCallback(() => {
        if (nodeEdgeMapper && scheduler && scheduler.isInitialized && programme) {
            return entities.map((entity) => getScheduleContextInfo({ entity, nodeEdgeMapper, scheduler, programme }));
        }
        else {
            return [
                {
                    broadcastDataMatches: false,
                    reportedConfigMatches: false,
                    requestedConfigDisabled: false,
                    requestedConfigMatches: false,
                    requestIsInFlight: false,
                },
            ];
        }
    }, [nemHash, scheduler, programme]);
    const loadContextFromDatastore = async (scheduler) => {
        dispatchTransactionState({ loading: true });
        debug && console.log(`WindowAllocation used effect calling loadContextFromDatastore`);
        const { err, data: context } = await scheduler.loadContextFromDatastore();
        if (err) {
        }
        if (context) {
            const action = { type: 'Recalculate', value: null };
            dispatch(action);
        }
        dispatchTransactionState({ loading: false });
    };
    /** on change of scheduler hash, `loadContextFromDatastore()` (if not initialized) */
    useEffect(() => {
        if (scheduler) {
            if (scheduler.isInitialized) {
                debug && console.log(`WindowAllocation scheduler.isInitialized ${scheduler.isInitialized}`);
            }
            else {
                dispatchTransactionState({ loading: true });
                debug && console.log(`WindowAllocation used effect calling loadContextFromDatastore`);
                loadContextFromDatastore(scheduler);
            }
        }
    }, [schedulerHash]);
    const selectWindow = {
        valueGetter: (value, row, column, apiRef) => inSelectedWindows({ id: row.id }),
        renderCell: (gridCellParams) => radioSelector({ gridCellParams, valueSetter: togglePresenceInSelectedObjectsShim }),
        renderHeader: () => (_jsx(SelectAll, { items: gridRows ?? [],
            selectedObjects: selectedWindows,
            setSelectedObjects: setSelectedWindows })),
    };
    const common = {
        sortable: false,
        resizable: false,
        filterable: false,
        disableColumnMenu: true,
    };
    const columns = [
        {
            field: 'id',
            ...common,
            ...selectWindow,
            width: 50,
        },
        {
            field: 'startTime',
            headerName: 'Start',
            ...common,
            width: 60,
            valueFormatter: (value) => {
                const date = new Date(value);
                return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
            },
        },
        {
            field: 'duration',
            headerName: 'Duration',
            ...common,
            width: 80,
            valueFormatter: (value) => `${value} min${value !== 1 ? 's' : ''}`,
        },
        {
            field: 'delay',
            headerName: 'Delay',
            ...common,
            width: 80,
            valueFormatter: (value) => `${value} min${value !== 1 ? 's' : ''}`,
        },
        {
            field: 'entities',
            headerName: 'Scheduled',
            ...common,
            minWidth: 250,
            flex: 1,
            valueGetter: (value) => summarizeList(value, 8),
        },
    ];
    const OnDemandWindowRows = gridRows ?? [];
    const handleDurationChange = (e) => {
        const val = parseInt(e.target.value);
        if (isNonNaNNumber(val) && isOnDemandProgrammeConstructorProps(programme)) {
            const action = {
                type: 'MutateProgramme',
                value: {
                    update: { ...programme, windowDuration: val },
                },
            };
            dispatch(action);
        }
    };
    const handleGapChange = (e) => {
        const val = parseInt(e.target.value);
        if (isNonNaNNumber(val) && isOnDemandProgrammeConstructorProps(programme)) {
            const action = {
                type: 'MutateProgramme',
                value: {
                    update: { ...programme, windowGapDuration: val },
                },
            };
            dispatch(action);
        }
    };
    const handleRepeatsChange = (e) => {
        const val = parseInt(e.target.value);
        if (isNonNaNNumber(val) &&
            isOnDemandProgrammeConstructorProps(programme) &&
            between(val, programme.getUserAttributes()['repeats']?.limits?.min ?? 1, (programme.getUserAttributes()['repeats']?.limits?.max ?? 1) + 1)) {
            const action = {
                type: 'MutateProgramme',
                value: {
                    update: { ...programme, repeats: val },
                },
            };
            dispatch(action);
        }
    };
    const changeState = scheduler?.getChangeState();
    const schedulerInitialized = scheduler !== null && scheduler.isInitialized;
    const changesArePresent = changeState !== undefined && (changeState.changed.length > 0 || changeState.new.length > 0 || changeState.deleted.length > 0);
    const canSave = schedulerInitialized && changesArePresent;
    const canLink = schedulerInitialized && changeState !== undefined && selectedItems.length > 0 && selectedWindows.length > 0;
    const canUnlink = schedulerInitialized &&
        changeState !== undefined &&
        selectedWindows.length > 0 &&
        (selectedItems.length > 0 || selectedWindows.map((w) => w.entities.length).reduce((s, c) => s + c) > 0);
    const canAddWindows = schedulerInitialized && selectedWindows.length === 1;
    const handlePersist = async () => {
        let persistHandled = null;
        if (scheduler) {
            dispatchTransactionState({ saving: true });
            console.log('handlePersist change state', scheduler.getChangeState());
            const res = await scheduler?.persistChangeState();
            console.log('handlePersist res', res);
            if (programme)
                UserFunctions.addObjectsToState([programme]);
            dispatchTransactionState({ saving: false });
            const action = { type: 'Recalculate', value: null };
            dispatch(action);
            persistHandled = true;
            if (res.writeRes.err || !res.writeRes.data)
                UserFunctions.setUserMessage({
                    id: v4(),
                    type: 'MessageBar',
                    label: 'OnDemandProgramme saved',
                    content: `Error saving ${programme?.name}: ${res.writeRes.err?.message}`,
                    severity: Severity.error,
                });
            else
                UserFunctions.setUserMessage({
                    id: v4(),
                    type: 'SnackBar',
                    label: 'OnDemandProgramme saved',
                    content: `Saved ${programme?.name}`,
                    severity: Severity.success,
                });
        }
        return persistHandled;
    };
    const handleLinkSelectedItemsToWindows = async ({ items, windowProxy }) => {
        if (windowProxy) {
            const action = {
                type: 'LinkItemsToWindow',
                value: { items, windowProxy },
            };
            if (scheduler) {
                dispatchTransactionState({ verifying: true });
                dispatch(action);
                dispatchTransactionState({ verifying: false });
            }
            else {
                console.log(`WARN: handleLinkSelectedItemsToSlots no scheduler`);
            }
        }
    };
    const handleUnLinkSelectedItemsFromWindows = async ({ items }) => {
        const action = {
            type: 'UnlinkItemsFromWindow',
            value: { items },
        };
        if (scheduler) {
            dispatchTransactionState({ verifying: true });
            dispatch(action);
            dispatchTransactionState({ verifying: false });
        }
        else {
            console.log(`WARN: handleLinkSelectedItemsToSlots no scheduler`);
        }
    };
    const handleFlushWindows = async ({ windowProxies }) => {
        const action = {
            type: 'FlushWindows',
            value: { windowProxies },
        };
        if (scheduler) {
            dispatchTransactionState({ verifying: true });
            dispatch(action);
            dispatchTransactionState({ verifying: false });
        }
        else {
            console.log(`WARN: handleLinkSelectedItemsToSlots no scheduler`);
        }
    };
    const handleInsertWindowAbove = ({ windowProxy }) => {
        if (windowProxy && isNonNaNNumber(windowProxy.window)) {
            const action = {
                type: 'InsertWindowAbove',
                value: { windowProxy },
            };
            if (scheduler) {
                dispatchTransactionState({ verifying: true });
                dispatch(action);
                dispatchTransactionState({ verifying: false });
            }
            else {
                console.log(`WARN: handleLinkSelectedItemsToSlots no scheduler`);
            }
        }
    };
    const handleInsertWindowBelow = ({ windowProxy }) => {
        if (windowProxy && isNonNaNNumber(windowProxy.window)) {
            const action = {
                type: 'InsertWindowBelow',
                value: { windowProxy },
            };
            if (scheduler) {
                dispatchTransactionState({ verifying: true });
                dispatch(action);
                dispatchTransactionState({ verifying: false });
            }
            else {
                console.log(`WARN: handleLinkSelectedItemsToSlots no scheduler`);
            }
        }
    };
    const handleRemoveWindow = ({ windowProxy }) => {
        if (windowProxy && isNonNaNNumber(windowProxy.window)) {
            const action = {
                type: 'RemoveWindow',
                value: { windowProxy },
            };
            if (scheduler) {
                dispatchTransactionState({ verifying: true });
                dispatch(action);
                dispatchTransactionState({ verifying: false });
            }
            else {
                console.log(`WARN: handleLinkSelectedItemsToSlots no scheduler`);
            }
        }
    };
    const handleReset = async () => {
        if (scheduler) {
            const newScheduler = scheduler.getLastPersistedSchedulerState();
            if (newScheduler) {
                const action = { type: 'Reset', value: { scheduler: newScheduler } };
                dispatch(action);
            }
        }
    };
    const dataTable = tableRows ? getDataTable({ dataTableRows: tableRows }) : null;
    const getRowClassName = useCallback((params) => {
        if (programme && scheduler)
            return getDataGridRowClassForWindow(params.row.window);
        return '';
    }, [programme, scheduler]);
    const components = {
        header: (_jsxs(Stack, { direction: isSmallView ? 'column' : 'row', justifyContent: 'space-between', alignItems: 'center', children: [_jsx(ProgrammeHeader, { programme: programme }), _jsx(Timezone, {})] })),
        timeline: programme && scheduler && scheduler.isInitialized ? (_jsx(EntityProgammeTimelineChart, { dataTable: dataTable, fragmentProps: { style: { height: 50 + 41, width: '100%' } }, timelineOptions: { timeline: { groupByRowLabel: true }, hAxis: { format: 'HH:mm' } } })) : (_jsx(Skeleton, { variant: "rounded", height: 41 + 'px', width: '100%', sx: { marginBlockEnd: '50px' } })),
        scheduleStatus: (_jsx(ScheduleStatus, { loaded: programme !== null && scheduler !== null && scheduler.isInitialized, status: getEntitySchedulesMatchesRequestedReported() })),
        windowControls: (_jsx(Controls, { programme,
            handleDurationChange,
            handleGapChange,
            handleRepeatsChange,
            disabled: !isAdmin })),
        transactionState: _jsx(TransactionIndicators, { transactionState, loaded: programme !== null }),
        rolling: programme && scheduler ? (_jsx(DataGrid, { rows: OnDemandWindowRows,
            columns,
            autoPageSize: true,
            maxRows: 10,
            density: 'compact',
            disableColumnMenu: true,
            disableColumnSorting: true,
            disableColumnResize: true,
            sortModel: [{ field: 'startTime', sort: 'asc' }],
            onRowClick: ({ row }) => (inSelectedWindows(row) ? removeSelectedWindow(row) : addSelectedWindows(row)),
            getRowClassName,
            sx: Object.values(getStylesForColorCodes(programme)) })) : (_jsx(Skeleton, { variant: "rounded", height: '100%', width: '100%' })),
        groupsAndEntities: contextCustomer && programme && programme.supportedType ? (_jsxs(Stack, { height: '100%', children: [_jsx(Typography, { variant: "h6", children: isSmallView ? `Schedulable Items` : `Schedulable Groups and ${pluralize(toTitleCase(programme.supportedType))}` }), _jsx(SimpleTreeView, { sx: { height: '100%', overflowY: 'auto' }, children: _jsx(ObjectTree, { selectable: isAdmin,
                        setSelectedNodeIds: (sks) => isAdmin &&
                            setSelectedItems(sks
                                .map((sk) => objectTreeNodeEdgeMapperRef.current.getItem(getZombieInstanceFromPrimaryKey(sk)))
                                .filter(existsFilter)),
                        selectedNodeIds: [...selectedItems.map((item) => item.sk)],
                        nodeEdgeMapper: objectTreeNodeEdgeMapperRef.current,
                        hideTopStep: true,
                        contextObjects: [contextCustomer],
                        queryPaths: [
                            {
                                stepQuery: {
                                    objectTypeId: 'Group',
                                    edgeTypeId: edgeTypeMatrix.Customer.Group.customer_has_group.edgeTypeId,
                                    adjacencyType: AdjacentType.CHILD,
                                    nodeFilters: [
                                        {
                                            filters: [{ key: 'supportedType', predicate: ComparisonType.EQUALS, value: programme.supportedType }],
                                            setOperation: LogicalOperatorType.AND,
                                        },
                                    ],
                                },
                                nextSteps: [
                                    {
                                        stepQuery: {
                                            objectTypeId: programme.supportedType,
                                            edgeTypeId: getGroupToEntityEdgeTypeId(programme.supportedType),
                                        },
                                    },
                                    ...getSingleTypeRecursivePath({
                                        groupObjectType: 'Group',
                                        containedObjectType: programme.supportedType,
                                        ContainedTypeInstanceClickWrapper: ItemTreeOnClickWrapper,
                                    }),
                                ],
                            },
                        ] }) }, "groupsAndEntities")] })) : (_jsx(Skeleton, { variant: "rounded", height: '100%', width: '100%' })),
        buttons: isAdmin ? (_jsx(Actions, { canAddAbove: canAddWindows,
            addAbove: () => handleInsertWindowAbove({ windowProxy: selectedWindows.at(0) }),
            canAddBelow: canAddWindows,
            addBelow: () => handleInsertWindowBelow({ windowProxy: selectedWindows.at(0) }),
            canLink,
            link: () => handleLinkSelectedItemsToWindows({ items: selectedItems, windowProxy: selectedWindows.at(0) }),
            canUnlink,
            unlink: () => handleFlushWindows({ windowProxies: selectedWindows }),
            canRemove: !canUnlink && canAddWindows && 1 < (programme?.windows ?? 1),
            remove: () => handleRemoveWindow({ windowProxy: selectedWindows.at(0) }),
            canReset: canSave,
            reset: () => handleReset(),
            canSave,
            save: handlePersist,
            loaded: programme !== null })) : (_jsx(_Fragment, {})),
        lastSent: isAdmin ? (_jsx(Stack, { direction: 'row', justifyContent: 'center', children: _jsx(Stack, { direction: isSmallView ? 'column' : 'row', alignItems: 'center', gap: 1, children: _jsxs(Stack, { direction: 'row', alignItems: 'center', gap: 1, children: [_jsx(Typography, { fontStyle: 'italic', children: "Last sent:" }), _jsx(Typography, { fontStyle: 'italic', children: programme?.appliedTimestamps && programme.appliedTimestamps.length > 0 ? getNiceDate(programme.appliedTimestamps?.at(0)) : 'Never' })] }) }) })) : (_jsx(_Fragment, {})),
        // leave for debug
        // changeState: (
        //   <Typography>{JSON.stringify({ new: changeState?.new, change: changeState?.changed, deleted: changeState?.deleted }, null, 1)}</Typography>
        // ),
    };
    return (_jsxs(Stack, { minHeight: isSmallView ? '200cqh' : isAdminRole(userSelectedGroup) ? `calc(100cqh - ${cardPadding * 2}px - ${headerHeight}px - ${tabPadding * 2}px)` : '100cqh', gap: 1, children: [_jsx(AdminCardWrapper, { children: _jsxs(Stack, { gap: 1, children: [components.header, components.scheduleStatus, components.timeline] }) }), _jsx(AdminCardWrapper, { cardProps: { sx: { flexGrow: 1, display: 'flex', flexDirection: 'row' } }, cardContentProps: { sx: { boxSizing: 'border-box', flexGrow: 1 } }, children: _jsxs(Stack, { height: '100%', flexGrow: 1, gap: 1, children: [components.transactionState, components.windowControls, _jsxs(Stack, { direction: isSmallView ? 'column' : 'row', flexGrow: 1, gap: 1, sx: { flexBasis: isSmallView ? '60.5em' : '30em', containerType: 'size' }, children: [_jsx("div", { style: { width: isSmallView ? '100cqw' : '50cqw', minHeight: '30em' }, children: components.rolling }), _jsx("div", { style: { width: isSmallView ? '100cqw' : '50cqw', minHeight: '30em' }, children: components.groupsAndEntities })] })] }) }), components.buttons, _jsx(Stack, { direction: isSmallView ? 'column' : 'row', justifyContent: 'center', gap: 1, children: components.lastSent })] }));
};
